import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { useSelect, useSwitch } from '@chilecompra/react-kit'
import { Breadcrumb, Breadcrumbs, DataTable, Select, Switch } from '@chilecompra/react-kit/components'
import {
  PROVIDERS_INDICATORS_DICTIONARY,
  PROVIDERS_INDICATORS_NAMES_DICTIONARY,
  PROVIDERS_INDICATORS_OPTIONS,
  PROVIDERS_INDICATORS_TRANSLATION_DICTIONARY
} from '../../config/settings/constants'
import routes from '../../config/settings/routes'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { onGetProvidersThunk } from './ProvidersSection.actions'
import Jumbotron from '../../components/Basics/Jumbotron'
import HorizontalBarChart from '../../components/Charts/HorizontalBarChart'
import RegionalPurchasingMechanisms from '../../components/Infographics/RegionalPurchasingMechanisms'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import {
  Content,
  Description,
  GridContainer,
  GridItem,
  Info,
  Italic,
  JumboDescription,
  JumboTitle,
  Link,
  Root,
  Title
} from './ProvidersSection.styles'

/**
 * The ProvidersSection' container.
 */
const ProvidersSection = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { endYear, latestUpdate, loading, sectors = [], startYear } = useSelector(state => state.topNavbar)
  const { categories, csv, csvTableTender, data, loading: loadingData } = useSelector(state => state.providersSection)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue =>
      dispatch(onGetProvidersThunk({ proccessId: indicatorValue, sectorId: sectorValue, year: updatedValue })),
    overwriteCallback: () => endYear
  })
  const { value: indicatorValue, onChange: handleChangeIndicator } = useSelect({
    initialValue: PROVIDERS_INDICATORS_DICTIONARY.amount,
    changeCallback: updatedValue =>
      dispatch(onGetProvidersThunk({ proccessId: updatedValue, sectorId: sectorValue, year: yearValue })),
    overwriteCallback: () => PROVIDERS_INDICATORS_DICTIONARY.amount
  })
  const { value: sectorValue, onChange: handleChangeSector } = useSelect({
    initialValue: 7,
    changeCallback: updatedValue =>
      dispatch(onGetProvidersThunk({ proccessId: indicatorValue, sectorId: updatedValue, year: yearValue }))
  })
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, csvTableTender, yearValue],
    captureImageCallback: () =>
      exportToImage('providers-horizontal-bar-chart', `Info_Principales_Proveedores_${yearValue}`),
    downloadFileCallback: () => {
      if (indicatorValue === 1) {
        exportToCsv(csv, `Info_Principales_Proveedores_${yearValue}`)
      } else if (indicatorValue === 2) {
        exportToCsv(csvTableTender, `Info_Principales_Proveedores_${yearValue}`)
      }
    }
  })
  const handleDataLabelFormatter = useCallback(
    function () {
      if (indicatorValue === PROVIDERS_INDICATORS_DICTIONARY.amount) {
        return currencyValue
          ? `<strong>USD$${formatAmount(this.y)}</strong>`
          : `<strong>$${formatAmount(this.y)}</strong>`
      }

      if (indicatorValue === PROVIDERS_INDICATORS_DICTIONARY.biddings) {
        return `<strong>${this.y}</strong>`
      }

      return `<strong>${this.y}</strong>`
    },
    [indicatorValue, currencyValue]
  )
  const handleLabelFormatter = useCallback(function () {
    const [records] = this.chart.series
    const row = records.userOptions.data[this.pos]
    return `
      <a href="${routes.provider.replace(
        /:dni/,
        row.dni
      )}" style="color: ${theme.palette.brand.primary}; text-decoration: underline; text-transform: uppercase;">${this.value}</a>
      <br />
      <span style="color: ${theme.palette.brand.black3}">${row.dni}</span>
    `
  }, [])

  let dataTableFields
  if (indicatorValue === 1) {
    dataTableFields = [
      { label: 'Año', value: 'year' },
      { label: 'Proveedor', value: 'name' },
      { label: 'Monto transado', value: 'amountCLP' },
      { label: 'Monto transado (USD)', value: 'amountUSD' },
      { label: 'N° de órdenes de compra', value: 'biddings' }
    ]
  } else if (indicatorValue === 2) {
    dataTableFields = [
      { label: 'Año', value: 'year' },
      { label: 'Proveedor', value: 'name' },
      { label: 'Nº de licitaciones adjudicadas', value: 'biddings' }
    ]
  }
  let headersCellsPropsDef
  if (indicatorValue === 1) {
    headersCellsPropsDef = [
      { enableSort: true, width: '10%' },
      { enableSort: true, width: '30%' },
      { enableSort: true, width: '15%' },
      { enableSort: true, width: '15%' },
      { enableSort: true, width: '15%' }
    ]
  } else if (indicatorValue === 2) {
    headersCellsPropsDef = [
      { enableSort: true, width: '10%' },
      { enableSort: true, width: '30%' },
      { enableSort: true, width: '15%' }
    ]
  }
  let recordsDecoratorsDef
  if (indicatorValue === 1) {
    recordsDecoratorsDef = [
      ({ year }) => year,
      ({ dni, name }) => (
        <Link style={{ textDecoration: 'underline' }} to={routes.provider.replace(/:dni/, dni)}>
          {name}
        </Link>
      ),
      ({ amountCLP }) => `$${formatAmount(amountCLP)}`,
      ({ amountUSD }) => `USD$${formatAmount(amountUSD)}`,
      ({ biddings }) => formatAmount(biddings)
    ]
  } else if (indicatorValue === 2) {
    recordsDecoratorsDef = [
      ({ year }) => year,
      ({ dni, name }) => (
        <Link style={{ textDecoration: 'underline' }} to={routes.provider.replace(/:dni/, dni)}>
          {name}
        </Link>
      ),
      ({ biddings }) => formatAmount(biddings)
    ]
  }

  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const sectionsOptions = useMemo(() => sectors.filter(sector => sector.value !== 8), [sectors])
  const getEnhancedData = useCallback(
    (currency, collection = []) =>
      collection.map(datum => {
        const updatedDatum = {
          ...datum,
          amount: currency ? datum.amountUSD : datum.amountCLP
        }
        return {
          ...datum,
          color: theme.palette.brand.primary,
          y: updatedDatum[PROVIDERS_INDICATORS_TRANSLATION_DICTIONARY[indicatorValue]]
        }
      }),
    [indicatorValue, currencyValue]
  )

  useEffect(() => {
    if (!endYear || loading) {
      return
    }

    dispatch(onGetProvidersThunk({ year: endYear, sectorId: sectorValue, proccessId: indicatorValue }))
  }, [endYear, loading])

  return (
    <Root>
      <GridContainer>
        <GridItem xs={12}>
          <Jumbotron>
            <Breadcrumbs>
              <Breadcrumb to={routes.root} variant="link">
                Datos Abiertos
              </Breadcrumb>
              <Breadcrumb>Proveedores</Breadcrumb>
            </Breadcrumbs>
            <JumboTitle>Proveedores</JumboTitle>
            <JumboDescription>
              Los proveedores del Estado son todas aquellas empresas que hacen
              <br /> negocios con los organismos públicos a través Mercado Público.
            </JumboDescription>
          </Jumbotron>
        </GridItem>
      </GridContainer>
      <Content>
        <GridContainer padding="0 0 23px">
          <GridItem xs={12}>
            <Title>Principales proveedores del Estado</Title>
          </GridItem>
        </GridContainer>
        <GridContainer padding="0 0 28px">
          <GridItem xs={12}>
            <Description>
              Conoce quienes son los proveedores con los mayores montos transados con el Estado, así como los
              proveedores que cuentan con el mayor número de licitaciones adjudicadas según el año seleccionado.
            </Description>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12}>
            <DataLayout
              disableCaptureImage={disableCaptureImage}
              disableDownloadFile={disableDownloadFile}
              header={
                <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                  <GridItem md={7} xs={12}>
                    <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                      <GridItem md={2} xs={12}>
                        <Select
                          label="AÑO"
                          loading={loading}
                          options={yearsOptions}
                          value={yearValue}
                          onChange={handleChangeYear}
                        />
                      </GridItem>
                      <GridItem md={4} xs={12}>
                        <Select
                          label="INDICADOR"
                          loading={loading}
                          options={PROVIDERS_INDICATORS_OPTIONS}
                          value={indicatorValue}
                          onChange={handleChangeIndicator}
                        />
                      </GridItem>
                      <GridItem md={4} xs={12}>
                        <Select
                          label="SECTOR DEL ESTADO"
                          loading={loading}
                          options={sectionsOptions}
                          value={sectorValue}
                          onChange={handleChangeSector}
                        />
                      </GridItem>
                      <GridItem md={2} xs={12} />
                    </GridContainer>
                  </GridItem>
                  <GridItem md={2} xs={12}>
                    <Switch
                      checked={currencyValue}
                      color="primary"
                      labelLeft="CLP"
                      labelRight="USD"
                      onChange={handleChangeCurrency}
                    />
                  </GridItem>
                </GridContainer>
              }
              infographic={<RegionalPurchasingMechanisms />}
              noData={data.length === 0 && !loadingData}
              noDataText="No hay datos de organismos proveedores para los filtros seleccionados."
              ref={dataLayoutRef}
              updatedDate={latestUpdate}
              onCaptureImage={handleCaptureImage}
              onChangeView={handleChangeView}
              onDownloadFile={handleDownloadFile}
            >
              <DataLayoutChild view={DataLayoutViews.CHART}>
                <GridContainer>
                  <GridItem xs={12}>
                    <HorizontalBarChart
                      categories={categories}
                      data={getEnhancedData(currencyValue, data)}
                      dataLabelFormatter={handleDataLabelFormatter}
                      labelFormatter={handleLabelFormatter}
                      id="providers-horizontal-bar-chart"
                    />
                  </GridItem>
                </GridContainer>
                <GridContainer margin="19px 0 0">
                  <GridItem xs={12}>
                    <Info>{PROVIDERS_INDICATORS_NAMES_DICTIONARY[indicatorValue]}</Info>
                  </GridItem>
                </GridContainer>
                <GridContainer margin="19px 0 0">
                  <GridItem xs={12}>
                    <Info>
                      <Italic>
                        La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto
                        + impuestos)
                      </Italic>
                    </Info>
                  </GridItem>
                </GridContainer>
              </DataLayoutChild>
              <DataLayoutChild view={DataLayoutViews.TABLE}>
                <GridContainer padding="0 0 34px">
                  <GridItem xs={12}>
                    <DataTable
                      headers={dataTableFields}
                      headersCellsProps={headersCellsPropsDef}
                      initialRows={10}
                      loading={loading}
                      records={getEnhancedData(currencyValue, data)}
                      recordsDecorators={recordsDecoratorsDef}
                    />
                  </GridItem>
                </GridContainer>
                <GridContainer margin="30px 0 0">
                  <GridItem xs={12}>
                    <Info>
                      <Italic>
                        La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto
                        + impuestos)
                      </Italic>
                    </Info>
                  </GridItem>
                </GridContainer>
              </DataLayoutChild>
            </DataLayout>
          </GridItem>
        </GridContainer>
      </Content>
    </Root>
  )
}

export default ProvidersSection
